import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, of, concatMap, tap, filter } from 'rxjs';
import { UserMgmtService } from '../../services/user-mgmt.service';
import { UserMgmtUiActions, UserMgmtApiActions } from '../actions/index';
import { IUserCreate, IUserDetail, IUserList, IUserUpdate } from '../../models/user-mgmt';
import { IDjangoErrors } from '../../../../shared/models/DjangoErrors';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserMgmtFacade } from '../../facade/user-mgmt.facade';
import { formatDjangoErrors } from '../../../../shared/utils/django-errors';
import { HttpErrorResponse } from '@angular/common/http';
import { DatasetDrawerState } from '../../../datasets/components/dataset-drawer/drawer-state';
import { LoginService } from '../../../authentication/services/login.service';
import { SessionTeardownService } from '../../../../shared/utils/clear-session-storage';
import { SessionActions } from '../../../../shared/state/session/session.actions';
import { SessionOwnerService } from '../../../../shared/services/session-owner.service';

@Injectable()
export class UserMgmtEffects {
  private actions$ = inject(Actions);
  private userMgmtService = inject(UserMgmtService);
  private toastService = inject(ToastService);
  private userMgmtFacade = inject(UserMgmtFacade);
  private loginService = inject(LoginService);
  private datasetDrawerState = inject(DatasetDrawerState);
  private sessionOwner = inject(SessionOwnerService);
  private sessionTeardown = inject(SessionTeardownService);

  getUserList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtUiActions.GetUsers),
      concatMap(() =>
        this.userMgmtService.getUsers().pipe(
          map((results: IUserList[]) => UserMgmtApiActions.GetUserListSuccess({ payload: results })),
          catchError((error) => {
            this.toastService.show('Failed to load user list', 'error');
            return of(UserMgmtApiActions.GetUserListFailure({ errors: error as IDjangoErrors }));
          })
        )
      )
    )
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtUiActions.GetUser),
      concatMap((action) =>
        this.userMgmtService.getUserById(action.id).pipe(
          map((results: IUserDetail) => UserMgmtApiActions.GetUserSuccess({ payload: results })),
          catchError((error) => {
            this.toastService.show('Failed to fetch user details', 'error');
            return of(UserMgmtApiActions.GetUserFailure({ errors: error as IDjangoErrors }));
          })
        )
      )
    )
  );

  postUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtUiActions.PostUser),
      concatMap(({ user }) =>
        this.userMgmtService.createUser(user).pipe(
          map((results: IUserCreate) => UserMgmtApiActions.PostUserSuccess({ payload: results })),
          catchError((error: HttpErrorResponse) => {
            const djangoErrors = error.error as IDjangoErrors;
            this.toastService.show(formatDjangoErrors(djangoErrors), 'error');
            return of(UserMgmtApiActions.PostUserFailure({ errors: djangoErrors }));
          })
        )
      )
    )
  );

  patchUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtUiActions.PatchUser),
      concatMap(({ user }) =>
        this.userMgmtService.updateUser(user).pipe(
          map((results: IUserUpdate) => UserMgmtApiActions.UpdateUserSuccess({ payload: results })),
          catchError((error) => {
            this.toastService.show('Failed to update user', 'error');
            return of(UserMgmtApiActions.UpdateUserFailure({ errors: error as IDjangoErrors }));
          })
        )
      )
    )
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtUiActions.DeleteUser),
      concatMap(({ id }) =>
        this.userMgmtService.deleteUser(id).pipe(
          map(() => UserMgmtApiActions.DeleteUserSuccess({ payload: id })),
          catchError((error) => {
            this.toastService.show('Failed to delete user', 'error');
            return of(UserMgmtApiActions.DeleteUserFailure({ errors: error as IDjangoErrors }));
          })
        )
      )
    )
  );

  reactivateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtUiActions.ReactivateUser),
      concatMap(({ id }) =>
        this.userMgmtService.reactivateUser(id).pipe(
          map((results: IUserDetail) => UserMgmtApiActions.ReactivateUserSuccess({ payload: results })),
          catchError((error) => {
            this.toastService.show('Failed to reactivate user', 'error');
            return of(UserMgmtApiActions.ReactivateUserFailure({ errors: error as IDjangoErrors }));
          })
        )
      )
    )
  );

  getMeUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtUiActions.GetMeUser),
      concatMap(() =>
        this.userMgmtService.getMeUser().pipe(
          map((results: IUserDetail) => UserMgmtApiActions.GetMeUserSuccess({ payload: results })),
          catchError((error: HttpErrorResponse) => {
            // Check if the error is due to inactive user
            const isUserInactive = error.error?.code === 'user_inactive' || 
                                   error.error?.detail?.toLowerCase().includes('inactive');
            
            if (isUserInactive) {
              // Don't show generic error, we'll handle this in a separate effect
              return of(UserMgmtApiActions.GetMeUserFailure({ errors: error.error as IDjangoErrors }));
            }
            
            this.toastService.show('Failed to fetch current user', 'error');
            return of(UserMgmtApiActions.GetMeUserFailure({ errors: error.error as IDjangoErrors }));
          })
        )
      )
    )
  );

  /**
   * Purge state that belongs to a different account.
   *
   * Persisted slices are written by whoever was signed in at the time, and
   * rehydration happens while the injector is built, long before the router,
   * postVerifyUser(), or any /me call. If /me answers with a different user
   * than the one that owns the persisted state, everything in the store
   * belongs to somebody else, so drop it and re-apply the user we just
   * fetched. Without this the header lab chip kept naming a lab from the
   * previous account.
   */
  resetStateOnUserChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtApiActions.GetMeUserSuccess),
      filter(({ payload }) => {
        const previousOwner = this.sessionOwner.read();
        this.sessionOwner.write(payload.id);
        return previousOwner !== null && previousOwner !== payload.id;
      }),
      // The reset clears getMeUser too, so put the fetched user straight back.
      concatMap(({ payload }) => [
        SessionActions.ResetSessionState(),
        UserMgmtApiActions.GetMeUserSuccess({ payload }),
      ])
    )
  );

  /**
   * Handle inactive user error by logging out and redirecting to login
   */
  getMeUserInactiveFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserMgmtApiActions.GetMeUserFailure),
        filter(({ errors }) => {
          const djangoErrors = errors as { code?: string; detail?: string };
          return djangoErrors?.code === 'user_inactive' || 
                 djangoErrors?.detail?.toLowerCase().includes('inactive') || false;
        }),
        tap(() => {
          this.toastService.show('Your account has been deactivated. Please contact an administrator.', 'error');
          // Clear session and redirect to login
          this.loginService.postLogout().subscribe({
            complete: () => {
              this.datasetDrawerState.setSelectedSequenceFileIds([]);
              this.sessionTeardown.clearStorage();
              this.sessionTeardown.reloadToLogin();
            },
            error: () => {
              this.datasetDrawerState.setSelectedSequenceFileIds([]);
              this.sessionTeardown.clearStorage();
              this.sessionTeardown.reloadToLogin();
            },
          });
        })
      ),
    { dispatch: false }
  );

  postUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtApiActions.PostUserSuccess),
      tap(() => this.toastService.show('User created successfully!', 'success')),
      map(() => UserMgmtUiActions.GetUsers())
    )
  );

  updateUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtApiActions.UpdateUserSuccess),
      tap(() => this.toastService.show('User updated successfully!', 'success')),
      concatMap(({ payload }) => [
        UserMgmtUiActions.GetUsers(),
        UserMgmtUiActions.GetUser({ id: payload.id.toString() }),
      ])
    )
  );

  deleteUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtApiActions.DeleteUserSuccess),
      tap(() => this.toastService.show('User deleted successfully!', 'success')),
      map(() => UserMgmtUiActions.GetUsers())
    )
  );

  reactivateUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserMgmtApiActions.ReactivateUserSuccess),
      tap(() => this.toastService.show('User reactivated successfully!', 'success')),
      concatMap(({ payload }) => [
        UserMgmtUiActions.GetUsers(),
        UserMgmtUiActions.GetUser({ id: payload.id.toString() }),
      ])
    )
  );
}
